/**
 * Sync parity — "does RDS still hold exactly what Supabase holds?"
 *
 * The Command Center next to this panel answers WHICH system serves a
 * screen. This one answers the other question: whether the copy in RDS and
 * the copy in Supabase still AGREE — table by table, column by column, row
 * by row and bucket by bucket — so an operator can see at a glance that the
 * sync is working, and see exactly where it is not.
 *
 * The comparison itself is computed on the NotesAI backend
 * (api/rds_parity.py): only the backend can read both sides. This panel
 * only presents what the report says.
 *
 * Same visual rules as the rest of the tab (see LAYOUT_CONTRACT.md):
 *   - every element that says where data comes from spells out SUPABASE or
 *     RDS; colour is the second channel and it says STATE, not identity
 *   - green = the two sides agree / the system is serving, red = they do
 *     not, orange = a difference that needs its sentence, grey = unknown
 *   - no PHI anywhere: names of tables and columns, counts, verdicts
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, HStack, VStack, Text, Button, Spinner, Alert, AlertIcon, Badge,
  Table, Thead, Tbody, Tr, Th, Td, TableContainer,
  Progress, SimpleGrid, useToast,
} from "@chakra-ui/react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as ChartTooltip, CartesianGrid,
  ResponsiveContainer, Cell, PieChart, Pie, Legend,
} from "recharts";
import { fetchRdsParity } from "../../services/rdsParity";
import { syncMirror } from "../../utils/rdsPhi";
import { PanelCard, Kpi, SectionLabel, StatePill, EmptyNote, SourceLegend } from "./rdsMigration/rdsUi";
import TwoWaySyncDialog from "./TwoWaySyncDialog";

/** The verdict of one table copy, and the word that goes with it. */
const TABLE_VERDICT = {
  MATCH: { label: "IN PARITY", tone: "green" },
  ROW_DIFF: { label: "ROW COUNT DIFFERS", tone: "orange" },
  COLUMN_DIFF: { label: "COLUMNS DIFFER", tone: "red" },
  MISSING_IN_RDS: { label: "MISSING IN RDS", tone: "red" },
  MISSING_IN_SUPABASE: { label: "MISSING IN SUPABASE", tone: "orange" },
  // A schema Supabase's data API does not serve: the app cannot use it and
  // the mirror cannot copy it. Listed, not compared -- unless it holds rows.
  NOT_EXPOSED: { label: "NOT EXPOSED BY SUPABASE", tone: "gray" },
  UNREACHABLE: { label: "IN SUPABASE, OUT OF REACH", tone: "red" },
  UNKNOWN: { label: "NOT COMPARED", tone: "gray" },
};

const BUCKET_VERDICT = {
  MATCH: { label: "IN PARITY", tone: "green" },
  DIFFERENT: { label: "OBJECT COUNTS DIFFER", tone: "red" },
  STALE: { label: "LAST SYNC FAILED", tone: "orange" },
  NOT_IN_SUPABASE: { label: "NOT IN SUPABASE", tone: "orange" },
  UNKNOWN: { label: "NOT COMPARED", tone: "gray" },
};

const n = (value) => (typeof value === "number" && Number.isFinite(value) ? value.toLocaleString() : "—");

/** One line of a count, with the system spelled out. */
function CountLine({ label, value, tone = "gray" }) {
  return (
    <HStack spacing={2} align="baseline">
      <Text fontSize="10px" fontWeight="bold" color="gray.400" letterSpacing="0.08em">
        {label}
      </Text>
      <Text fontSize="sm" fontWeight="bold" color={tone === "gray" ? "gray.800" : `${tone}.700`}>
        {value}
      </Text>
    </HStack>
  );
}

/**
 * A list of column names with its own count. The names are names, never
 * values — this is what the operator compares by eye.
 */
function ColumnList({ title, tone, items, emptyText }) {
  const names = (items || []).map((c) => c.name || c);
  return (
    <Box>
      <HStack spacing={2} mb={1}>
        <StatePill tone={tone}>{title}</StatePill>
        <Text fontSize="xs" fontWeight="bold" color="gray.700">{names.length}</Text>
      </HStack>
      {names.length === 0 ? (
        <EmptyNote>{emptyText}</EmptyNote>
      ) : (
        <HStack spacing={1} flexWrap="wrap">
          {names.map((name) => (
            <Badge key={name} colorScheme={tone} variant="subtle" fontSize="9px" px={1.5} borderRadius="md">
              {name}
            </Badge>
          ))}
        </HStack>
      )}
    </Box>
  );
}

/** The headline: do the two databases agree? */
function VerdictCard({ report }) {
  const verdict = report.verdict;
  const inParity = verdict === "IN_PARITY";
  const partial = verdict === "NOT_FULLY_COMPARED";
  const tone = inParity ? "green" : partial ? "orange" : "red";
  const label = inParity ? "IN PARITY" : partial ? "NOT FULLY COMPARED" : "DIFFERENCES FOUND";
  const s = report.summary || {};
  const body = inParity
    ? `RDS and Supabase hold the same PHI: ${n(s.tables_matching)} of ${n(s.tables)} tables and ${n(s.columns_matched)} of ${n(s.columns_checked)} columns match, and every bucket holds every object Supabase can serve.`
    : partial
      ? `No difference was found in what could be compared, but ${n(s.not_compared)} part(s) of the estate could not be read — the figures below are only as good as that.`
      : `${n(s.differences)} difference${s.differences === 1 ? "" : "s"} between RDS and Supabase. The rows below say where.`;
  const notExposed = s.schemas_not_exposed || [];
  return (
    <PanelCard tone={tone} testid="parity-verdict">
      <HStack spacing={4} align="flex-start" flexWrap="wrap">
        <Box flexShrink={0}>
          <StatePill tone={tone} size="sm">{label}</StatePill>
          <Text fontSize="sm" fontWeight="bold" color="gray.700" mt={2} maxW="520px">{body}</Text>
          {!inParity && !partial && (
            <Text fontSize="xs" color="gray.500" mt={1} maxW="520px" data-testid="parity-auto-repair-note">
              RDS repairs these on its own: rows on every mirror run (5 minutes), columns, keys and
              missing tables on its hourly pass and right after a sync or merge. Repair now runs it at once.
            </Text>
          )}
          {notExposed.length > 0 && (
            <Text fontSize="xs" color="gray.500" mt={1} maxW="520px" data-testid="parity-not-exposed-note">
              Not compared: {notExposed.join(", ")} — Supabase&apos;s data API does not expose
              {notExposed.length === 1 ? " this schema" : " these schemas"}, so neither the app nor the mirror can reach
              {notExposed.length === 1 ? " it" : " them"}.
            </Text>
          )}
        </Box>
        <HStack spacing={3} flexWrap="wrap" ml="auto">
          <CountLine label="SUPABASE" value={`${n(report.sources?.supabase?.schemas)} schemas · ${n(report.sources?.supabase?.tables)} tables`} />
          <CountLine label="RDS" value={`${n(report.sources?.rds?.schemas)} schemas · ${n(report.sources?.rds?.tables)} tables`} />
        </HStack>
      </HStack>
      <HStack spacing={4} mt={3} flexWrap="wrap">
        <SourceLegend items={[
          { label: "Both sides agree", tone: "green" },
          { label: "Rows differ", tone: "orange" },
          { label: "Columns differ / missing", tone: "red" },
          { label: "Not compared", tone: "gray" },
        ]} />
      </HStack>
    </PanelCard>
  );
}

/** The numbers, in one row. */
function ParityKpis({ report }) {
  const s = report.summary || {};
  const rowsMatch = s.rows_supabase === s.rows_rds;
  return (
    <SimpleGrid columns={{ base: 2, md: 3, xl: 6 }} spacing={3}>
      <Kpi label="Tables compared" value={n(s.tables)} hint={`${n(s.tables_matching)} in parity`} tone="gray" />
      <Kpi label="PHI columns checked" value={n(s.columns_checked)} hint={`${n(s.columns_matched)} match`} tone="blue" />
      <Kpi label="Column match" value={`${n(s.pct)}%`} hint={`${n(s.differences)} differences`} tone={s.differences ? "red" : s.not_compared ? "orange" : "green"} />
      <Kpi label="Rows compared" value={n(s.rows_supabase)} hint={`RDS holds ${n(s.rows_rds)}`} tone={rowsMatch ? "green" : "orange"} />
      <Kpi label="Buckets" value={n(s.buckets)} hint={`${n(s.buckets_matching)} in parity`} tone="gray" />
      <Kpi label="Checked at" value={(report.generated_at || "").replace("T", " ").replace("Z", "")} hint={`${n(report.duration_ms)} ms`} tone="gray" />
    </SimpleGrid>
  );
}

/**
 * The chart: one stacked bar per table — how many of its columns match,
 * and how many do not. Green is agreement, red is a gap, orange is a type
 * that drifted.
 */
function ParityChart({ tables }) {
  const data = useMemo(() => (tables || []).map((t) => {
    const sum = t.summary || {};
    return {
      table: t.table,
      matched: sum.columns_matched || 0,
      type: sum.columns_type_mismatch || 0,
      missing: sum.columns_missing_in_rds || 0,
      extra: sum.columns_extra_in_rds || 0,
      verdict: t.verdict,
    };
  }), [tables]);

  const withColumns = data.filter((d) => (d.matched + d.type + d.missing + d.extra) > 0);
  if (!withColumns.length) {
    return <EmptyNote>No table had a comparable column to chart.</EmptyNote>;
  }

  const height = Math.max(180, withColumns.length * 26 + 40);

  return (
    <Box data-testid="parity-chart">
      <Box maxH="420px" overflowY="auto" pr={1}>
        <ResponsiveContainer width="100%" height={height}>
          <BarChart data={withColumns} layout="vertical" margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 10, fill: "#6B7280" }} allowDecimals={false} />
            <YAxis type="category" dataKey="table" width={150} tick={{ fontSize: 10, fill: "#374151" }} />
            <ChartTooltip
              formatter={(value, name) => [n(value), name]}
              contentStyle={{ fontSize: 11, borderRadius: 8 }}
            />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Bar dataKey="matched" stackId="cols" fill="#38A169" name="Matching" radius={[0, 0, 0, 0]} />
            <Bar dataKey="type" stackId="cols" fill="#DD6B20" name="Type mismatch" />
            <Bar dataKey="missing" stackId="cols" fill="#E53E3E" name="Missing in RDS" />
            <Bar dataKey="extra" stackId="cols" fill="#805AD5" name="Extra in RDS" />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}

/** The same split as a donut: how much of the comparison agrees. */
function ParityDonut({ report }) {
  const s = report.summary || {};
  const data = [
    { name: "Matching", value: s.columns_matched || 0, color: "#38A169" },
    { name: "Missing in RDS", value: s.columns_missing_in_rds || 0, color: "#E53E3E" },
    { name: "Type mismatch", value: s.columns_type_mismatch || 0, color: "#DD6B20" },
    { name: "Extra in RDS", value: s.columns_extra_in_rds || 0, color: "#805AD5" },
  ].filter((d) => d.value > 0);
  if (!data.length) return <EmptyNote>No column was compared.</EmptyNote>;
  return (
    <Box w="100%" h="190px" data-testid="parity-donut">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" innerRadius="55%" outerRadius="80%" paddingAngle={2}>
            {data.map((d) => <Cell key={d.name} fill={d.color} />)}
          </Pie>
          <ChartTooltip formatter={(value, name) => [n(value), name]} contentStyle={{ fontSize: 11, borderRadius: 8 }} />
          <Legend wrapperStyle={{ fontSize: 10 }} />
        </PieChart>
      </ResponsiveContainer>
    </Box>
  );
}

/** One table copy: the schema, the two row counts, the column verdict. */
function SchemaDetail({ row }) {
  const c = row.columns || {};
  const verdict = TABLE_VERDICT[row.verdict] || TABLE_VERDICT.UNKNOWN;
  if (row.verdict === "NOT_EXPOSED" || row.verdict === "UNREACHABLE") {
    return (
      <Box borderLeftWidth="3px" borderLeftColor={`${verdict.tone}.300`} pl={3} py={1}
        data-testid={`parity-unexposed-${row.schema}`}>
        <HStack spacing={3} flexWrap="wrap" mb={1}>
          <Text fontSize="xs" fontWeight="bold" color="gray.700">{row.schema}</Text>
          <StatePill tone={verdict.tone}>{verdict.label}</StatePill>
          <CountLine label="SUPABASE ROWS" value={n(row.supabase?.rows)} />
          <CountLine label="RDS ROWS" value={row.rds?.exists ? n(row.rds?.rows) : "no copy"} />
        </HStack>
        <Text fontSize="xs" color="gray.600">
          Supabase&apos;s data API does not expose the {row.schema} schema, so neither the app nor the RDS mirror
          can read it.{" "}
          {row.verdict === "UNREACHABLE"
            ? "Supabase holds rows here that no copy can reach."
            : "It holds no rows."}{" "}
          If it is a real clinic, add it under Supabase → Project Settings → API → Exposed schemas: RDS creates its
          tables on its next hourly pass.
        </Text>
      </Box>
    );
  }
  return (
    <Box borderLeftWidth="3px" borderLeftColor={`${verdict.tone}.300`} pl={3} py={1}>
      <HStack spacing={3} flexWrap="wrap" mb={2}>
        <Text fontSize="xs" fontWeight="bold" color="gray.700">{row.schema}</Text>
        <StatePill tone={verdict.tone}>{verdict.label}</StatePill>
        <CountLine label="SUPABASE ROWS" value={n(row.supabase?.rows)} />
        <CountLine label="RDS ROWS" value={n(row.rds?.rows)} tone={row.rows_match === false ? "orange" : "gray"} />
        {row.rows_match === false && (
          <StatePill tone="orange">
            {Math.abs((row.supabase?.rows || 0) - (row.rds?.rows || 0))} row(s) apart
          </StatePill>
        )}
      </HStack>
      <VStack align="stretch" spacing={2}>
        <ColumnList
          title="IN BOTH"
          tone="green"
          items={c.matched}
          emptyText="No column of this table is in both copies."
        />
        {(c.missing_in_rds?.length > 0 || c.type_mismatch?.length > 0 || c.extra_in_rds?.length > 0) && (
          <HStack spacing={6} align="flex-start" flexWrap="wrap">
            <ColumnList
              title="MISSING IN RDS"
              tone="red"
              items={c.missing_in_rds}
              emptyText="Every expected PHI column is in RDS."
            />
            <ColumnList
              title="TYPE MISMATCH"
              tone="orange"
              items={c.type_mismatch}
              emptyText="No column type drifted."
            />
            <ColumnList
              title="EXTRA IN RDS"
              tone="purple"
              items={c.extra_in_rds}
              emptyText="RDS holds no column Supabase lacks."
            />
          </HStack>
        )}
        {c.not_mirrored_count > 0 && (
          <Box>
            <Text fontSize="10px" color="gray.400" mb={1}>
              {c.not_mirrored_count} column(s) on Supabase are not PHI and are never mirrored — expected, not a difference.
            </Text>
          </Box>
        )}
      </VStack>
    </Box>
  );
}

/** One table, expandable to every schema copy of it. */
function TableRow({ table }) {
  const [open, setOpen] = useState(false);
  const s = table.summary || {};
  const verdict = TABLE_VERDICT[table.verdict] || TABLE_VERDICT.UNKNOWN;
  // Every copy that is not in parity gets its own detail; the ones that
  // are only get counted.
  const offCopies = (table.schemas || []).filter((row) => row.verdict !== "MATCH");
  const matchingCopies = (table.schemas || []).length - offCopies.length;

  return (
    <Box borderBottomWidth="1px" borderBottomColor="gray.100" _last={{ borderBottomWidth: 0 }}>
      <HStack
        as="button"
        type="button"
        w="100%"
        px={3}
        py={2.5}
        spacing={3}
        justify="space-between"
        onClick={() => setOpen((v) => !v)}
        _hover={{ bg: "gray.50" }}
        textAlign="left"
        aria-expanded={open}
        data-testid={`parity-table-${table.table}`}
      >
        <HStack spacing={3} minW={0}>
          <Text fontSize="10px" color="gray.400" w="12px">{open ? "▾" : "▸"}</Text>
          <Text fontSize="xs" fontWeight="bold" color="gray.800" noOfLines={1}>{table.table}</Text>
          {table.phi_table ? (
            <Badge colorScheme="blue" variant="subtle" fontSize="9px" px={1.5}>PHI</Badge>
          ) : (
            <Badge colorScheme="gray" variant="subtle" fontSize="9px" px={1.5}>NOT PHI</Badge>
          )}
          <StatePill tone={verdict.tone}>{verdict.label}</StatePill>
        </HStack>
        <HStack spacing={4} flexShrink={0}>
          <CountLine label="COLUMNS" value={`${n(s.columns_matched)}/${n(s.columns_checked)}`} />
          <CountLine label="SUPABASE ROWS" value={n(s.rows_supabase)} />
          <CountLine label="RDS ROWS" value={n(s.rows_rds)} tone={s.rows_supabase !== s.rows_rds ? "orange" : "gray"} />
        </HStack>
      </HStack>
      {open && (
        <Box px={3} pb={3} pl={7}>
          {offCopies.length ? (
            <VStack align="stretch" spacing={3}>
              {offCopies.map((row) => <SchemaDetail key={row.schema} row={row} />)}
              {matchingCopies > 0 && (
                <Text fontSize="xs" color="gray.500">
                  {matchingCopies} other schema cop{matchingCopies === 1 ? "y" : "ies"} of this table match:
                  same columns, same row count on both sides.
                </Text>
              )}
            </VStack>
          ) : (
            <Text fontSize="xs" color="gray.500">
              Every schema copy of this table matches: same columns, same row count on both sides.
            </Text>
          )}
        </Box>
      )}
    </Box>
  );
}

/** The bucket comparison: Supabase's object count against RDS's. */
function BucketTable({ buckets }) {
  if (!buckets?.length) {
    return <EmptyNote>No bucket was read from RDS.</EmptyNote>;
  }
  return (
    <TableContainer>
      <Table size="sm" variant="simple">
        <Thead>
          <Tr>
            <Th fontSize="10px">Bucket</Th>
            <Th fontSize="10px" isNumeric>Supabase objects</Th>
            <Th fontSize="10px" isNumeric title="Listed by Supabase, but every download fails: nothing to copy">
              Unservable in Supabase
            </Th>
            <Th fontSize="10px" isNumeric>RDS objects</Th>
            <Th fontSize="10px" isNumeric>Difference</Th>
            <Th fontSize="10px" isNumeric>Bytes in RDS</Th>
            <Th fontSize="10px">Last synced</Th>
            <Th fontSize="10px">Verdict</Th>
          </Tr>
        </Thead>
        <Tbody>
          {buckets.map((b) => {
            const verdict = BUCKET_VERDICT[b.verdict] || BUCKET_VERDICT.UNKNOWN;
            return (
              <Tr key={b.bucket} data-testid={`parity-bucket-${b.bucket}`}>
                <Td fontSize="xs" fontWeight="bold" color="gray.800">{b.bucket}</Td>
                <Td fontSize="xs" isNumeric>{n(b.source_objects)}</Td>
                <Td fontSize="xs" isNumeric color="gray.500">{b.unservable_objects ? n(b.unservable_objects) : "—"}</Td>
                <Td fontSize="xs" isNumeric>{n(b.rds_objects)}</Td>
                <Td fontSize="xs" isNumeric color={b.delta ? "red.600" : "gray.600"}>
                  {b.delta === null || b.delta === undefined ? "—" : (b.delta > 0 ? `+${b.delta}` : b.delta)}
                </Td>
                <Td fontSize="xs" isNumeric>{n(b.total_bytes)}</Td>
                <Td fontSize="xs" color="gray.600">
                  {b.last_synced_at ? `${b.last_synced_at.replace("T", " ").replace("+00:00", " UTC")}` : "never"}
                  {b.error ? <Text fontSize="10px" color="red.600">{b.error}</Text> : null}
                </Td>
                <Td><StatePill tone={verdict.tone}>{verdict.label}</StatePill></Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </TableContainer>
  );
}

/**
 * What the app itself saw: the frontend overlay reads every row and every
 * file from both sides and compares them. These are those counts — the live
 * proof that the two copies agree, not a snapshot.
 */
function RuntimeEvidence({ runtime }) {
  const tables = runtime?.tables || [];
  const buckets = runtime?.buckets || [];
  if (!tables.length && !buckets.length) {
    return <EmptyNote>No comparison has been reported by the app yet.</EmptyNote>;
  }
  return (
    <VStack align="stretch" spacing={3}>
      {tables.length > 0 && (
        <Box>
          <SectionLabel>Tables read from both sides</SectionLabel>
          <TableContainer>
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th fontSize="10px">Table</Th>
                  <Th fontSize="10px" isNumeric>Reads</Th>
                  <Th fontSize="10px" isNumeric>Matched</Th>
                  <Th fontSize="10px" isNumeric>Mismatched</Th>
                  <Th fontSize="10px" isNumeric>Missing</Th>
                  <Th fontSize="10px" isNumeric>Extra</Th>
                </Tr>
              </Thead>
              <Tbody>
                {tables.map((t) => (
                  <Tr key={`t-${t.table}`}>
                    <Td fontSize="xs" fontWeight="bold" color="gray.800">{t.table}</Td>
                    <Td fontSize="xs" isNumeric>{n(t.reads)}</Td>
                    <Td fontSize="xs" isNumeric color="green.700">{n(t.matched)}</Td>
                    <Td fontSize="xs" isNumeric color={t.mismatched ? "red.600" : "gray.600"}>{n(t.mismatched)}</Td>
                    <Td fontSize="xs" isNumeric color={t.missing ? "orange.600" : "gray.600"}>{n(t.missing)}</Td>
                    <Td fontSize="xs" isNumeric color={t.extra ? "orange.600" : "gray.600"}>{n(t.extra)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      )}
      {buckets.length > 0 && (
        <Box>
          <SectionLabel>Files read from both sides</SectionLabel>
          <TableContainer>
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th fontSize="10px">Bucket</Th>
                  <Th fontSize="10px" isNumeric>Reads</Th>
                  <Th fontSize="10px" isNumeric>Served by RDS</Th>
                  <Th fontSize="10px" isNumeric>Fell back to Supabase</Th>
                  <Th fontSize="10px" isNumeric>Mismatched</Th>
                </Tr>
              </Thead>
              <Tbody>
                {buckets.map((b) => (
                  <Tr key={`b-${b.bucket}`}>
                    <Td fontSize="xs" fontWeight="bold" color="gray.800">{b.bucket}</Td>
                    <Td fontSize="xs" isNumeric>{n(b.reads)}</Td>
                    <Td fontSize="xs" isNumeric color="green.700">{n(b.served_rds)}</Td>
                    <Td fontSize="xs" isNumeric color={b.fell_back ? "orange.600" : "gray.600"}>{n(b.fell_back)}</Td>
                    <Td fontSize="xs" isNumeric color={b.mismatched ? "red.600" : "gray.600"}>{n(b.mismatched)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </VStack>
  );
}

/**
 * The panel. It runs the comparison when it is opened and whenever the
 * operator asks for a fresh one.
 */
export default function SyncParityPanel() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deepOpen, setDeepOpen] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const toast = useToast();

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchRdsParity();
      setReport(result);
      const s = result?.summary || {};
      const inParity = result?.verdict === "IN_PARITY";
      const partial = result?.verdict === "NOT_FULLY_COMPARED";
      toast({
        title: inParity
          ? "RDS and Supabase are in parity"
          : partial ? "Only part of the estate could be compared" : "Differences found",
        description: `${n(s.tables_matching)} of ${n(s.tables)} tables in parity · ${n(s.columns_matched)} of ${n(s.columns_checked)} columns match · ${n(s.differences)} difference(s)`,
        status: inParity ? "success" : partial ? "warning" : "warning",
        duration: 7000,
        isClosable: true,
      });
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { run(); }, [run]);

  // The repair is RDS's own sync, now instead of on its schedule. An
  // operator run always takes the create pass, so it brings back the rows
  // AND each copy's shape (missing / extra / retyped columns, missing
  // tables, the key) -- the catch-up instead while RDS is independent.
  // Then the comparison runs again.
  const repair = useCallback(async () => {
    setRepairing(true);
    try {
      const result = await syncMirror();
      const s = result?.summary || {};
      if (result?.mode === "supabase_only") {
        toast({
          title: "RDS is paused",
          description: "RDS pulls nothing from Supabase while paused (Use Supabase only), so nothing was repaired.",
          status: "warning", duration: 9000, isClosable: true,
        });
      } else {
        toast({
          title: result?.ok ? "RDS repaired from Supabase" : "Repair finished with problems",
          description: `${n(s.ok)} of ${n(s.tables)} tables refreshed`
            + (s.reshaped ? ` · ${s.reshaped} reshaped` : "")
            + (s.created ? ` · ${s.created} created` : "")
            + (result?.skipped_full_mirror ? " · catch-up only (RDS is independent)" : "")
            + (result?.error ? ` — ${result.error}` : ""),
          status: result?.ok ? "success" : "warning",
          duration: 9000,
          isClosable: true,
        });
      }
    } catch (err) {
      toast({
        title: "Repair failed",
        description: err?.message || String(err),
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    } finally {
      setRepairing(false);
    }
    run();
  }, [toast, run]);

  if (loading && !report) {
    return (
      <VStack align="center" spacing={3} py={10} data-testid="parity-loading">
        <Spinner color="blue.500" />
        <Text fontSize="sm" color="gray.600">Comparing RDS with Supabase — tables, columns, rows and buckets…</Text>
        <Text fontSize="xs" color="gray.400">
          This reads every mirrored table on both sides, so it takes a moment.
        </Text>
      </VStack>
    );
  }

  if (error && !report) {
    return (
      <Alert status="error" borderRadius="lg" data-testid="parity-error">
        <AlertIcon />
        The comparison could not be completed: {error}
      </Alert>
    );
  }

  if (!report) return null;

  const tables = report.tables || [];
  const differences = tables.filter((t) => t.verdict !== "MATCH");

  return (
    <>
      <VStack align="stretch" spacing={4} data-testid="rds-parity-panel">
      <HStack justify="space-between" align="center" flexWrap="wrap" spacing={3}>
        <Box>
          <Text fontSize="sm" fontWeight="bold" color="gray.700">Sync parity</Text>
          <Text fontSize="xs" color="gray.500">
            The same PHI, in Supabase and in RDS — table by table, column by column, row by row.
          </Text>
        </Box>
        <HStack spacing={2}>
          {loading && <Spinner size="sm" color="blue.500" />}
          <Button
            size="sm"
            variant={report.verdict === "DIFFERENCES" ? "solid" : "outline"}
            colorScheme="orange"
            onClick={repair}
            isLoading={repairing}
            loadingText="Repairing…"
            isDisabled={loading}
            title="Run RDS's sync now: rows, missing tables and columns, column types and keys, from Supabase"
            data-testid="parity-repair"
          >
            Repair now
          </Button>
          <Button
            size="sm"
            variant="outline"
            colorScheme="purple"
            onClick={() => setDeepOpen(true)}
            data-testid="parity-deep-diff"
          >
            Row-by-row diff
          </Button>
          <Button
            size="sm"
            colorScheme="blue"
            onClick={run}
            isLoading={loading}
            loadingText="Comparing…"
            data-testid="parity-refresh"
          >
            Run comparison again
          </Button>
        </HStack>
      </HStack>

      {error && (
        <Alert status="warning" borderRadius="lg">
          <AlertIcon />
          The last comparison failed: {error} — the figures below are from the previous run.
        </Alert>
      )}

      {(report.warnings || []).length > 0 && (
        <Alert status="info" borderRadius="lg">
          <AlertIcon />
          <VStack align="stretch" spacing={0.5}>
            {report.warnings.map((w, i) => <Text key={i} fontSize="xs">{w}</Text>)}
          </VStack>
        </Alert>
      )}

      <VerdictCard report={report} />

      <ParityKpis report={report} />

      <SimpleGrid columns={{ base: 1, xl: 3 }} spacing={4}>
        <PanelCard title="Columns by table" subtitle="How many of each table's columns match, and which do not" testid="parity-chart-card">
          <ParityChart tables={tables} />
        </PanelCard>
        <PanelCard title="Column split" subtitle="Every column compared, both sides" testid="parity-donut-card">
          <ParityDonut report={report} />
        </PanelCard>
        <PanelCard title="Rows" subtitle="Supabase against RDS" testid="parity-rows-card">
          <VStack align="stretch" spacing={3}>
            <CountLine label="SUPABASE ROWS" value={n(report.summary?.rows_supabase)} />
            <CountLine label="RDS ROWS" value={n(report.summary?.rows_rds)} tone={report.summary?.rows_supabase === report.summary?.rows_rds ? "gray" : "orange"} />
            <Progress
              value={100}
              colorScheme={report.summary?.rows_supabase === report.summary?.rows_rds ? "green" : "orange"}
              size="sm"
              borderRadius="full"
            />
            <Text fontSize="xs" color="gray.500">
              {report.summary?.rows_supabase === report.summary?.rows_rds
                ? "Both databases hold the same number of rows."
                : "The two databases do not hold the same number of rows."}
            </Text>
          </VStack>
        </PanelCard>
      </SimpleGrid>

      <PanelCard
        title="PHI tables"
        subtitle={`${n(report.summary?.tables_matching)} of ${n(report.summary?.tables)} in parity — open a table for the column detail`}
        testid="parity-tables-card"
      >
        {tables.length === 0 ? (
          <EmptyNote>No table was compared.</EmptyNote>
        ) : (
          <VStack align="stretch" spacing={0}>
            {(differences.length ? [...differences, ...tables.filter((t) => t.verdict === "MATCH")] : tables)
              .map((t) => <TableRow key={t.table} table={t} />)}
          </VStack>
        )}
      </PanelCard>

      <PanelCard
        title="Buckets"
        subtitle="Supabase Storage against the copy RDS holds"
        testid="parity-buckets-card"
      >
        <BucketTable buckets={report.buckets} />
      </PanelCard>

      <PanelCard
        title="What the app saw"
        subtitle="Every read the frontend overlay compared against the other side"
        testid="parity-runtime-card"
      >
        <RuntimeEvidence runtime={report.runtime} />
      </PanelCard>
      </VStack>

      {/* The row-by-row diff, for when the comparison above says something
          is off: RDS compares every row of every table against Supabase and
          shows which column of which row differs. It writes nothing until
          the operator reviews the plan and applies it. */}
      <TwoWaySyncDialog
        isOpen={deepOpen}
        onClose={() => setDeepOpen(false)}
        onApplied={run}
        modes={["tables", "buckets"]}
      />
    </>
  );
}
